import React, { useEffect, useState } from 'react';
import { Line, Bar } from 'react-chartjs-2';
import axios from 'axios';
import {
  Chart,
  LineElement,
  BarElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

// Register elements
Chart.register(LineElement, BarElement, CategoryScale, LinearScale, PointElement, Title, Tooltip, Legend);

interface ServerStats {
  system: {
    memoryUsagePercent: number;
    loadAverage: number;
    uptimeHours: number;
    activeConnections: number;
  };
  activity: {
    activeUsersLastHour: number;
    activeUsersLastDay: number;
    recentBattlesLastHour: number;
  };
  database: {
    users: number;
    mercenaries: number;
    battles: number;
    tournaments: number;
    estimatedSizeMB: string;
  };
  usage: {
    diskUsagePercent: string;
    bandwidthUsagePercent: number;
  };
}

const ServerMetrics: React.FC = () => {
  const [serverStats, setServerStats] = useState<ServerStats>({
    system: {
      memoryUsagePercent: 0,
      loadAverage: 0,
      uptimeHours: 0,
      activeConnections: 0,
    },
    activity: {
      activeUsersLastHour: 0,
      activeUsersLastDay: 0,
      recentBattlesLastHour: 0,
    },
    database: {
      users: 0,
      mercenaries: 0,
      battles: 0,
      tournaments: 0,
      estimatedSizeMB: '0',
    },
    usage: {
      diskUsagePercent: '0',
      bandwidthUsagePercent: 0,
    },
  });

  useEffect(() => {
    const fetchServerStats = async () => {
      try {
        const response = await axios.get('/api/admin/server-stats');
        setServerStats(response.data);
      } catch (error) {
        console.error('Error fetching server statistics', error);
      }
    };

    fetchServerStats();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchServerStats, 30000);
    return () => clearInterval(interval);
  }, []);

  // Database size data
  const dbData = {
    labels: ['Users', 'Mercenaries', 'Battles', 'Tournaments'],
    datasets: [
      {
        label: 'Database Records',
        backgroundColor: ['#36a2eb', '#ffce56', '#ff6384', '#4bc0c0'],
        data: [
          serverStats.database.users,
          serverStats.database.mercenaries,
          serverStats.database.battles,
          serverStats.database.tournaments,
        ],
      },
    ],
  };

  // Activity data
  const activityData = {
    labels: ['Last Hour', 'Last Day'],
    datasets: [
      {
        label: 'Active Users',
        backgroundColor: '#00bfff',
        data: [serverStats.activity.activeUsersLastHour, serverStats.activity.activeUsersLastDay],
      },
    ],
  };

  const getStatusColor = (percentage: number) => {
    if (percentage < 50) return '#32cd32'; // Green
    if (percentage < 80) return '#ffdd57'; // Yellow
    return '#ff6384'; // Red
  };

  return (
    <>
      <h3>Server Health & Activity</h3>

      {/* System Health */}
      <div style={{ marginBottom: '15px' }}>
        <h4 style={{ margin: '0 0 10px 0', color: '#ffd700', fontSize: '1rem', textAlign: 'center' }}>System Health</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
          <div style={{ padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
              <span>Memory Usage</span>
              <span style={{ color: getStatusColor(serverStats.system.memoryUsagePercent) }}>
                {serverStats.system.memoryUsagePercent}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '6px',
              backgroundColor: '#555',
              borderRadius: '3px',
              marginTop: '4px'
            }}>
              <div style={{
                width: `${serverStats.system.memoryUsagePercent}%`,
                height: '100%',
                backgroundColor: getStatusColor(serverStats.system.memoryUsagePercent),
                borderRadius: '3px'
              }}></div>
            </div>
          </div>

          <div style={{ padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
              <span>Disk Usage</span>
              <span style={{ color: getStatusColor(parseFloat(serverStats.usage.diskUsagePercent)) }}>
                {serverStats.usage.diskUsagePercent}%
              </span>
            </div>
            <div style={{
              width: '100%',
              height: '6px',
              backgroundColor: '#555',
              borderRadius: '3px',
              marginTop: '4px'
            }}>
              <div style={{
                width: `${serverStats.usage.diskUsagePercent}%`,
                height: '100%',
                backgroundColor: getStatusColor(parseFloat(serverStats.usage.diskUsagePercent)),
                borderRadius: '3px'
              }}></div>
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '8px' }}>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h5 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>Load Avg</h5>
            <p style={{ margin: 0, fontSize: '1.1em', color: '#f8f0c6' }}>{serverStats.system.loadAverage}</p>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h5 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>Uptime</h5>
            <p style={{ margin: 0, fontSize: '1.1em', color: '#f8f0c6' }}>{serverStats.system.uptimeHours}h</p>
          </div>
          <div style={{ textAlign: 'center', padding: '8px', backgroundColor: '#1c1c29', borderRadius: '6px', border: '1px solid #6d4e27' }}>
            <h5 style={{ margin: '0 0 3px 0', color: '#ffd700', fontSize: '0.8rem' }}>DB Conn</h5>
            <p style={{ margin: 0, fontSize: '1.1em', color: '#f8f0c6' }}>{serverStats.system.activeConnections}</p>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', height: '200px' }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#ffd700', fontSize: '0.9rem', textAlign: 'center' }}>User Activity</h4>
          <div style={{ flex: 1 }}>
            <Bar
              data={activityData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { color: '#f8f0c6', font: { size: 10 } },
                    grid: { color: '#6d4e27' }
                  },
                  x: {
                    ticks: { color: '#f8f0c6', font: { size: 10 } },
                    grid: { color: '#6d4e27' }
                  }
                }
              }}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#ffd700', fontSize: '0.9rem', textAlign: 'center' }}>Database Records</h4>
          <div style={{ flex: 1 }}>
            <Bar
              data={dbData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { display: false },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: { color: '#f8f0c6', font: { size: 10 } },
                    grid: { color: '#6d4e27' }
                  },
                  x: {
                    ticks: { color: '#f8f0c6', font: { size: 10 } },
                    grid: { color: '#6d4e27' }
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      <div style={{ marginTop: '10px', textAlign: 'center', fontSize: '0.8em', color: '#888' }}>
        <p>DB Size: {serverStats.database.estimatedSizeMB} MB | Recent Battles: {serverStats.activity.recentBattlesLastHour}</p>
      </div>
    </>
  );
};

export default ServerMetrics;
